import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import Select from "react-select";
import { getUserProfile } from "@/store/slices/userSlice";
import { fetchCountryMobileCode } from "@/store/slices/internalUsersSlice";
import {
  CountryOption,
  CountrySingleValue,
} from "@/components/CountrySelectComponents";
import Toaster from "@/components/Toaster";
import { updateDeliveryProfile } from "@/store/slices/newDeliverySlice";
import { useNavigate } from "react-router-dom";
import ShowLoader from "@/components/Loader";
import ScrollToTop from "@/components/ScrollToTop";
import Swal from "sweetalert2";
import { deleteOtherDocument } from "@/store/slices/customerSlice";
import { useTranslation } from "react-i18next";

/* ---------------- COMMON REGEX (From SignUp) ---------------- */
const noHtmlRegex = /^(?!.*<[^>]+>).*$/;
const notBlankRegex = /\S/;
const noCodeRegex =
  /^(?!.*(javascript:|<script|<\/script>|<style|<\/style>|<iframe|<\/iframe>)).*$/i;
const noConsecutiveSpecialChars =
  /^(?!.*([!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])\1).*$/;
const noLeadingTrailingPunct =
  /^(?!^[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])(?<![!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]$).*$/;
const noLeadingSpaceRegex = /^[^\s]/;

/* ---------------- FILE VALIDATION HELPER ---------------- */
const fileValidation = (label, required = false, t) =>
  Yup.mixed()
    .nullable()
    .test(
      "fileSize",
      t("file_size_less_than_10mb"),
      (value) => {
        if (!value || !(value instanceof File)) return true;
        return value.size <= 10 * 1024 * 1024;
      }
    )
    .test(
      "fileType",
      t("only_jpg_png_pdf_allowed"),
      (value) => {
        if (!value || !(value instanceof File)) return true;
        const allowedTypes = [
          "image/jpeg",
          "image/png",
          "application/pdf",
        ];
        return allowedTypes.includes(value.type);
      }
    );

/* ---------------- COMMON STRING SECURITY ---------------- */
const secureString = (t) =>
  Yup.string()
    .trim()
    .matches(notBlankRegex, t("this_field_cannot_be_empty_spaces"))
    .matches(noLeadingSpaceRegex, t("leading_spaces_not_allowed"))
    .matches(noHtmlRegex, t("html_tags_not_allowed"))
    .matches(noCodeRegex, t("code_content_not_allowed"))
    .matches(
      noConsecutiveSpecialChars,
      t("consecutive_special_characters_not_allowed")
    )
    .matches(
      noLeadingTrailingPunct,
      t("cannot_start_end_punctuation")
    );

/* ---------------- VALIDATION SCHEMA (Synchronized with SignUp) ---------------- */
const validationSchema = (t) =>
  Yup.object({
    first_name: secureString(t)
      .required(t("first_name_required"))
      .min(2, t("first_name_min"))
      .max(50, t("first_name_max"))
      .matches(/^[a-zA-Z\s]+$/, t("only_alphabets_allowed")),

    last_name: Yup.string()
      .transform((value) => (value === "" ? undefined : value))
      .notRequired()
      .concat(secureString(t))
      .min(2, t("last_name_min"))
      .max(50, t("last_name_max"))
      .matches(/^[a-zA-Z\s]+$/, t("only_alphabets_are_allowed")),

    email: Yup.string()
      .trim()
      .lowercase()
      .required(t("email_required"))
      .matches(
        noLeadingSpaceRegex,
        t("leading_spaces_not_allowed")
      )
      .matches(
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        t("valid_email")
      )
      .max(100, t("email_max"))
      .matches(noHtmlRegex, t("invalid_characters_detected")),

    mobile_number: Yup.string()
      .trim()
      .required(t("mobile_number_required"))
      .matches(
        noLeadingSpaceRegex,
        t("leading_spaces_not_allowed")
      )
      .matches(/^[0-9]+$/, t("only_digits_allowed"))
      .min(8, t("mobile_number_min"))
      .max(15, t("mobile_number_max")),

    country_code: Yup.string().required(t("required")),

    address: secureString(t)
      .required(t("address_required"))
      .min(10, t("address_min"))
      .max(200, t("address_max")),

    gender: Yup.string()
      .nullable()
      .transform((value) => (value === "" ? null : value))
      .required(t("gender_required"))
      .oneOf(
        ["Male", "Female", "Other"],
        t("valid_gender")
      ),

    residence_id: secureString(t)
      .required(t("residence_id_required"))
      .min(2, t("residence_id_length"))
      .max(30, t("residence_id_length")),

    // driving_licence: secureString(t)
    //   .required("Driving licence required")
    //   .min(5, "Driving licence too short")
    //   .matches(/^[0-9A-Z]+$/i, "Only letters and numbers allowed"),

    // vehicle_number: secureString(t)
    //   .notRequired()
    //   .matches(/^[0-9A-Z\s-]+$/i, {
    //     message: "Invalid vehicle number format",
    //     excludeEmptyString: true,
    //   }),

    vehicle_number: Yup.string()
      .transform((value) => (value === "" ? undefined : value))
      .notRequired()
      .matches(/^[0-9A-Z\s-]+$/i, {
        message: t("invalid_vehicle_number"),
        excludeEmptyString: true,
      })
      .min(4, t("vehicle_number_min"))
      .max(20, t("vehicle_number_max")),

    vehicle_certificate_image: fileValidation(
      "Vehicle number certificate file",
      false,
      t
    ),

    // insurance: secureString(t)
    //   .notRequired()
    //   .min(5, "Insurance policy number too short"),

    residence_file: fileValidation(
      "Residence ID",
      true,
      t
    ),

    license_file: fileValidation(
      "License",
      true,
      t
    ),

    insurance_file: fileValidation(
      "Insurance",
      true,
      t
    ),

    other_documents: Yup.mixed()
      .nullable()
      .test(
        "fileType",
        t("only_pdf_image_allowed"),
        (value) => {
          if (!value) return true;

          const files = Array.isArray(value)
            ? value
            : [value];

          return files.every((file) => {
            if (typeof file === "string") return true;

            if (
              file?.file &&
              typeof file.file === "string"
            ) {
              return true;
            }

            if (file instanceof File) {
              return (
                file.type.startsWith("image/") ||
                file.type === "application/pdf"
              );
            }

            return false;
          });
        }
      )
      .test(
        "fileCount",
        t("upload_up_to_10_files"),
        (value) => {
          if (!value) return true;

          const files = Array.isArray(value)
            ? value
            : [value];

          return files.length <= 10;
        }
      ),

    other_documents_detail: Yup.string()
      .nullable()
      .test(
        "not-only-spaces",
        t("cannot_contain_only_spaces"),
        (value) => {
          if (value === null || value === undefined)
            return true;

          if (value === "") return true;

          return value.trim().length > 0;
        }
      )
      .matches(
        noHtmlRegex,
        t("html_tags_not_allowed")
      )
      .matches(
        noCodeRegex,
        t("code_content_not_allowed")
      )
      .matches(
        noConsecutiveSpecialChars,
        t("other_documents_special_characters")
      )
      .matches(
        noLeadingTrailingPunct,
        t("other_documents_punctuation")
      )
      .max(
        100,
        t("other_documents_max")
      ),
  });

export default function EditProfile() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { t } = useTranslation("delivery");

  const { loading } = useSelector(
    (state) => state.delivery
  );

  const { user } = useSelector(
    (state) => state.user
  );

  const { mobile_country_code } = useSelector(
    (state) => state.internalUser
  );

  const [previewUrl, setPreviewUrl] = useState(null);
  const [showSuccessModal, setShowSuccessModal] =
    useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors, isDirty },
  } = useForm({
    resolver: yupResolver(validationSchema(t)),
    mode: "onChange",
  });

  // Sanitizer function (From SignUp)
  const handleNoLeadingSpace = (e, fieldName) => {
    const value = e.target.value;

    if (value.startsWith(" ")) {
      setValue(fieldName, value.trimStart());
    }
  };

  const [removedFiles, setRemovedFiles] =
    useState({
      residence: false,
      license: false,
      insurance: false,
    });

  useEffect(() => {
    dispatch(getUserProfile());
    dispatch(fetchCountryMobileCode());
  }, [dispatch]);

  useEffect(() => {
    if (user) {
      reset({
        first_name: user?.first_name || "",
        last_name: user?.last_name || "",
        email: user?.email || "",
        mobile_number:
          user?.mobile_number || "",
        gender: user?.gender || "",
        country_code:
          user?.country_code || "",
        address:
          user?.kyc_details?.permanent_address ||
          "",
        residence_id:
          user?.residence_id || "",
        driving_licence:
          user?.kyc_details?.driving_licence ||
          "",
        vehicle_number:
          user?.kyc_details?.vehicle_number ||
          "",
        vehicle_certificate_image:
          user?.kyc_details
            ?.vehicle_certificate_image || "",
        insurance:
          user?.kyc_details?.insurance || "",
        other_documents:
          user?.other_docs || [],
        other_documents_detail:
          user?.other_documents_detail || null,
      });
    }
  }, [user, reset]);

  const onFormSubmit = (data) => {
    // 1. Guard clause: If nothing changed, do nothing.
    // This prevents unnecessary API calls and toasts.
    // if (!isDirty) return;
    if (!isDirty) {
      Toaster.success(
        t("profile_updated_successfully")
      );
      return;
    }

    const payload = {
      ...data,
      last_name: data.last_name ?? "",
      gender: data.gender,
      permanent_address: data.address,
      country_id: user?.country_id,
      city_id: user?.city_id,
      residence_image: data.residence_file,
      driving_licence_image:
        data.license_file,
      insurance_image:
        data.insurance_file,
      vehicle_certificate_image:
        data?.vehicle_certificate_image,
      other_documents:
        data?.other_documents || [],
      other_documents_detail:
        data?.other_documents_detail || null,
    };

    dispatch(updateDeliveryProfile(payload))
      .unwrap()
      .then((res) => {
        // 2. We remove the toast entirely as requested.
        // 3. We show the modal immediately on success.
        // Even if the backend doesn't send 'logout_required',
        // we show the modal to tell the user their account is now pending.
        if (res?.status !== "active") {
          setShowSuccessModal(true);
        } else {
          Toaster.success(
            t("profile_updated_successfully")
          );
          navigate("/delivery-partner/home");
        }
      })
      .catch((err) => {
        // Error toast remains for actual failures
        // (like network or validation errors)
        Toaster.error(
          err?.message ||
            t("failed_to_update_profile")
        );
      });
  };

  const removeFile = (field, stateKey) => {
    setValue(field, null);

    setRemovedFiles((prev) => ({
      ...prev,
      [stateKey]: true,
    }));
  };

  const handleFileChange = (e, field) => {
    const files = Array.from(e.target.files);

    if (field === "other_documents") {
      const existingDocs =
        watch("other_documents") || [];

      setValue(
        "other_documents",
        [...existingDocs, ...files],
        {
          shouldValidate: true,
        }
      );
    } else {
      setValue(field, files[0], {
        shouldValidate: true,
      });
    }
  };

  const other_documents =
    watch("other_documents") || [];

  const removeDocument = (
    index,
    documentId = null
  ) => {
    const updated = [...other_documents];

    const removedDoc =
      updated.splice(index, 1);

    setValue("other_documents", updated);

    if (documentId) {
      Swal.fire({
        title: t("are_you_sure"),
        text: t("you_wont_be_able_to_revert"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: t("yes_delete_it"),
      }).then((result) => {
        if (result.isConfirmed) {
          dispatch(
            deleteOtherDocument(documentId)
          )
            .unwrap()
            .then(() => {
              Toaster.success(
                t(
                  "other_document_deleted_successfully"
                )
              );
            })
            .catch((error) => {
              Toaster.error(
                t("failed_to_delete_document")
              );

              setValue(
                "other_documents",
                [...updated, removedDoc[0]]
              );
            });
        } else {
          setValue(
            "other_documents",
            [...updated, removedDoc[0]]
          );
        }
      });
    }
  };

  const inputClass = (error) =>
    `w-full mt-2 border ${
      error
        ? "border-red-500"
        : "border-gray-300"
    } rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#065C36]/20`;

  const FileBadge = ({
    fileName,
    onRemove,
    fileUrl,
    fileObject,
  }) => {
    if (!fileName) return null;

    const handlePreview = () => {
      if (fileObject instanceof File) {
        setPreviewUrl(
          URL.createObjectURL(fileObject)
        );
      } else if (fileUrl) {
        setPreviewUrl(fileUrl);
      }
    };

    return (
      <div className="flex items-center justify-between bg-[#E8F0FE] rounded-md px-3 py-2 mt-2 w-full max-w-md">
        <span
          className="text-[#1A73E8] text-sm font-medium truncate flex-1 underline mr-4 cursor-pointer"
          onClick={handlePreview}
        >
          {fileName}
        </span>

        <div className="flex items-center gap-3 shrink-0">
          <button
            type="button"
            onClick={handlePreview}
            className="text-[#F4B11F] hover:opacity-80"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
              <circle
                cx="12"
                cy="12"
                r="3"
              />
            </svg>
          </button>

          <button
            type="button"
            onClick={onRemove}
            className="text-[#EF4444] hover:opacity-80"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line
                x1="18"
                y1="6"
                x2="6"
                y2="18"
              />
              <line
                x1="6"
                y1="6"
                x2="18"
                y2="18"
              />
            </svg>
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {loading && <ShowLoader />}

      <ScrollToTop />

      <div className="bg-[#065C36] text-white py-5 sm:py-6 relative px-4 sm:px-8">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="absolute left-4 sm:left-8 top-4 flex items-center gap-2.5 text-base font-medium hover:underline transition-all opacity-90 hover:opacity-100"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line
              x1="19"
              y1="12"
              x2="5"
              y2="12"
            ></line>

            <polyline points="12 19 5 12 12 5"></polyline>
          </svg>

          {t("back")}
        </button>

        <div className="text-center mt-2">
          <h2 className="text-2xl font-bold">
            {t("edit_profile_info")}
          </h2>

          <p className="text-sm mt-1 opacity-90 max-w-2xl mx-auto">
            {t("edit_profile_description")}
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto mt-4 sm:mt-6 px-4 sm:px-8 pb-12">
        <form
          onSubmit={handleSubmit(onFormSubmit)}
          className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6"
        >
          {/* First Name */}
          <div>
            <label className="text-sm font-medium">
              {t("first_name")}{" "}
              <span className="text-red-500">
                *
              </span>
            </label>

            <input
              {...register("first_name", {
                onChange: (e) =>
                  handleNoLeadingSpace(
                    e,
                    "first_name"
                  ),
              })}
              className={inputClass(
                errors.first_name
              )}
            />

            {errors.first_name && (
              <p className="text-red-500 text-xs mt-1">
                {errors.first_name.message}
              </p>
            )}
          </div>

          {/* Last Name */}
          <div>
            <label className="text-sm font-medium">
              {t("last_name")}{" "}
            </label>

            <input
              {...register("last_name", {
                onChange: (e) =>
                  handleNoLeadingSpace(
                    e,
                    "last_name"
                  ),
              })}
              className={inputClass(
                errors.last_name
              )}
            />

            {errors.last_name && (
              <p className="text-red-500 text-xs mt-1">
                {errors.last_name.message}
              </p>
            )}
          </div>

          {/* Mobile */}
          <div>
            <label className="text-sm font-medium">
              {t("mobile_number")}{" "}
              <span className="text-red-500">
                *
              </span>
            </label>

            <div className="flex mt-2">
              <div className="w-[140px]">
                <Select
                  options={mobile_country_code}
                  value={mobile_country_code?.find(
                    (c) =>
                      c.value ===
                      watch("country_code")
                  )}
                  onChange={(val) =>
                    setValue(
                      "country_code",
                      val.value,
                      {
                        shouldValidate: true,
                      }
                    )
                  }
                  components={{
                    Option: CountryOption,
                    SingleValue:
                      CountrySingleValue,
                  }}
                  styles={{
                    control: (base) => ({
                      ...base,
                      height: "50px",
                      borderRight: "0",
                      borderRadius:
                        "8px 0 0 8px",
                    }),
                  }}
                />
              </div>

              <input
                {...register("mobile_number", {
                  onChange: (e) => {
                    handleNoLeadingSpace(
                      e,
                      "mobile_number"
                    );

                    const onlyNums =
                      e.target.value.replace(
                        /[^0-9]/g,
                        ""
                      );

                    setValue(
                      "mobile_number",
                      onlyNums,
                      {
                        shouldValidate: true,
                      }
                    );
                  },
                })}
                className="w-full border border-gray-300 rounded-r-lg px-4 py-3 focus:outline-none focus:ring-2"
                placeholder={t(
                  "mobile_number"
                )}
              />
            </div>

            {(errors.mobile_number ||
              errors.country_code) && (
              <p className="text-red-500 text-xs mt-1">
                {errors.mobile_number?.message ||
                  errors.country_code?.message}
              </p>
            )}
          </div>

          {/* Email */}
          <div>
            <label className="text-sm font-medium">
              {t("email")}{" "}
              <span className="text-red-500">
                *
              </span>
            </label>

            <input
              {...register("email", {
                onChange: (e) =>
                  handleNoLeadingSpace(
                    e,
                    "email"
                  ),
              })}
              className={inputClass(
                errors.email
              )}
            />

            {errors.email && (
              <p className="text-red-500 text-xs mt-1">
                {errors.email.message}
              </p>
            )}
          </div>

          {/* --- Address & Gender Section --- */}
          <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            {/* Address Field */}
            <div>
              <label className="text-sm font-medium">
                {t("address")}{" "}
                <span className="text-red-500">
                  *
                </span>
              </label>

              <input
                {...register("address", {
                  onChange: (e) =>
                    handleNoLeadingSpace(
                      e,
                      "address"
                    ),
                })}
                className={inputClass(
                  errors.address
                )}
                placeholder={t(
                  "enter_your_address"
                )}
              />

              {errors.address && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.address.message}
                </p>
              )}
            </div>

            {/* Gender Field */}
            <div>
              <label className="text-sm font-medium">
                {t("gender")}{" "}
                <span className="text-red-500">
                  *
                </span>
              </label>

              <div className="mt-2">
                <Select
                  placeholder={t(
                    "select_gender"
                  )}
                  options={[
                    {
                      value: "Male",
                      label: t("male"),
                    },
                    {
                      value: "Female",
                      label: t("female"),
                    },
                    {
                      value: "Other",
                      label: t("other"),
                    },
                  ]}
                  onChange={(val) =>
                    setValue(
                      "gender",
                      val?.value,
                      {
                        shouldValidate: true,
                        shouldDirty: true,
                      }
                    )
                  }
                  value={[
                    {
                      value: "Male",
                      label: t("male"),
                    },
                    {
                      value: "Female",
                      label: t("female"),
                    },
                    {
                      value: "Other",
                      label: t("other"),
                    },
                  ].find(
                    (c) =>
                      c.value ===
                      watch("gender")
                  ) || null}
                  isClearable
                  styles={{
                    control: (base) => ({
                      ...base,
                      height: "50px",
                      borderRadius: "8px",
                      borderColor:
                        errors.gender
                          ? "#EF4444"
                          : "#D1D5DB",
                      boxShadow: "none",
                    }),
                  }}
                />
              </div>

              {errors.gender && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.gender.message}
                </p>
              )}
            </div>
          </div>

          {/* Residence ID */}
          <div>
            <label className="text-sm font-medium">
              {t("residence_id")}{" "}
              <span className="text-red-500">
                *
              </span>
            </label>

            <div className="relative mt-2">
              <input
                {...register("residence_id", {
                  onChange: (e) =>
                    handleNoLeadingSpace(
                      e,
                      "residence_id"
                    ),
                })}
                className="w-full border border-gray-300 rounded-lg px-4 py-3 pr-44 focus:outline-none focus:ring-2"
              />

              <label className="absolute right-0 bottom-0 cursor-pointer">
                <img
                  src="/img/icons/choose_file.png"
                  className="h-[49px]"
                  alt={t("choose_file")}
                />

                <input
                  type="file"
                  hidden
                  onChange={(e) =>
                    setValue(
                      "residence_file",
                      e.target.files[0],
                      {
                        shouldValidate: true,
                        shouldDirty: true,
                      }
                    )
                  }
                />
              </label>
            </div>

            {errors.residence_id && (
              <p className="text-red-500 text-xs mt-1">
                {errors.residence_id.message}
              </p>
            )}

            {errors.residence_file && (
              <p className="text-red-500 text-xs mt-1">
                {errors.residence_file.message}
              </p>
            )}

            <FileBadge
              fileName={
                watch("residence_file")
                  ?.name ||
                (!removedFiles.residence &&
                  user?.residence_image
                    ?.split("/")
                    .pop())
              }
              fileUrl={
                !removedFiles.residence
                  ? user?.residence_image
                  : null
              }
              fileObject={watch(
                "residence_file"
              )}
              onRemove={() =>
                removeFile(
                  "residence_file",
                  "residence"
                )
              }
            />
          </div>

          {/* Driving License */}
          <div>
            <label className="text-sm font-medium">
              {t("driving_license")}{" "}
              <span className="text-red-500">
                *
              </span>
            </label>

            <div className="relative mt-2">
              {/* <input 
                // {...register("driving_licence", { onChange: (e) => handleNoLeadingSpace(e, "driving_licence") })} 
                readOnly
                className="w-full border border-gray-300 rounded-lg px-4 py-3 pr-44 focus:outline-none focus:ring-2 cursor-not-allowed" 
              /> */}

              <input
                readOnly
                value={
                  watch("license_file")
                    ?.name ||
                  (!removedFiles.license &&
                    user?.kyc_details
                      ?.driving_licence_image
                      ?.split("/")
                      .pop()) ||
                  ""
                }
                placeholder={t(
                  "upload_driving_license"
                )}
                className="w-full border border-gray-300 rounded-lg px-4 py-3 pr-44 focus:outline-none focus:ring-2 cursor-not-allowed"
              />

              <label className="absolute right-0 bottom-0 cursor-pointer">
                <img
                  src="/img/icons/choose_file.png"
                  className="h-[49px]"
                  alt={t("choose_file")}
                />

                <input
                  type="file"
                  hidden
                  onChange={(e) =>
                    setValue(
                      "license_file",
                      e.target.files[0],
                      {
                        shouldValidate: true,
                        shouldDirty: true,
                      }
                    )
                  }
                />
              </label>
            </div>

            {errors.driving_licence && (
              <p className="text-red-500 text-xs mt-1">
                {
                  errors.driving_licence
                    .message
                }
              </p>
            )}

            {errors.license_file && (
              <p className="text-red-500 text-xs mt-1">
                {
                  errors.license_file.message
                }
              </p>
            )}

            <FileBadge
              fileName={
                watch("license_file")
                  ?.name ||
                (!removedFiles.license &&
                  user?.kyc_details
                    ?.driving_licence_image
                    ?.split("/")
                    .pop())
              }
              fileUrl={
                !removedFiles.license
                  ? user?.kyc_details
                      ?.driving_licence_image
                  : null
              }
              fileObject={watch(
                "license_file"
              )}
              onRemove={() =>
                removeFile(
                  "license_file",
                  "license"
                )
              }
            />
          </div>

          {/* Vehicle */}
          {/* <div>
            <label className="text-sm font-medium">
              {t("vehicle_number")}
            </label>
            <input 
                {...register("vehicle_number", { onChange: (e) => handleNoLeadingSpace(e, "vehicle_number") })} 
                className={inputClass(errors.vehicle_number)} 
            />
            {errors.vehicle_number && <p className="text-red-500 text-xs mt-1">{errors.vehicle_number.message}</p>}
          </div> */}

          <div>
            <label className="text-sm font-medium">
              {t("vehicle_number")}{" "}
            </label>

            <div className="relative mt-2">
              <input
                {...register("vehicle_number", {
                  onChange: (e) =>
                    handleNoLeadingSpace(
                      e,
                      "vehicle_number"
                    ),
                })}
                className="w-full border border-gray-300 rounded-lg px-4 py-3 pr-44 focus:outline-none focus:ring-2"
                placeholder={t(
                  "enter_vehicle_number_upload_certificate"
                )}
              />

              <label className="absolute right-0 bottom-0 cursor-pointer">
                <img
                  src="/img/icons/choose_file.png"
                  className="h-[49px]"
                  alt={t("choose_file")}
                />

                <input
                  type="file"
                  hidden
                  onChange={(e) =>
                    setValue(
                      "vehicle_certificate_image",
                      e.target.files[0],
                      {
                        shouldValidate: true,
                        shouldDirty: true,
                      }
                    )
                  }
                />
              </label>
            </div>

            {errors.vehicle_number && (
              <p className="text-red-500 text-xs mt-1">
                {
                  errors.vehicle_number
                    .message
                }
              </p>
            )}

            {errors.vehicle_certificate_image && (
              <p className="text-red-500 text-xs mt-1">
                {
                  errors
                    .vehicle_certificate_image
                    .message
                }
              </p>
            )}

            <FileBadge
              fileName={
                watch(
                  "vehicle_certificate_image"
                )?.name ||
                (!removedFiles.residence &&
                  user?.kyc_details
                    ?.vehicle_certificate_image
                    ?.split("/")
                    .pop())
              }
              fileUrl={
                !removedFiles.vehicle
                  ? user?.kyc_details
                      ?.vehicle_certificate_image
                  : null
              }
              fileObject={watch(
                "vehicle_certificate_image"
              )}
              onRemove={() =>
                removeFile(
                  "vehicle_certificate_image",
                  "vehicle"
                )
              }
            />
          </div>

          {/* Insurance */}
          <div>
            <label className="text-sm font-medium">
              {t("insurance")}
            </label>

            <div className="relative mt-2">
              {/* <input 
              readOnly
                // {...register("insurance", { onChange: (e) => handleNoLeadingSpace(e, "insurance") })} 
                className="w-full border border-gray-300 rounded-lg px-4 py-3 pr-44 focus:outline-none focus:ring-2 cursor-not-allowed" 
              /> */}

              <input
                readOnly
                value={
                  watch("insurance_file")
                    ?.name ||
                  (!removedFiles.insurance &&
                    user?.kyc_details
                      ?.insurance_image
                      ?.split("/")
                      .pop()) ||
                  ""
                }
                placeholder={t(
                  "upload_insurance"
                )}
                className="w-full border border-gray-300 rounded-lg px-4 py-3 pr-44 focus:outline-none focus:ring-2 cursor-not-allowed"
              />

              <label className="absolute right-0 bottom-0 cursor-pointer">
                <img
                  src="/img/icons/choose_file.png"
                  className="h-[49px]"
                  alt={t("choose_file")}
                />

                <input
                  type="file"
                  hidden
                  onChange={(e) =>
                    setValue(
                      "insurance_file",
                      e.target.files[0],
                      {
                        shouldValidate: true,
                        shouldDirty: true,
                      }
                    )
                  }
                />
              </label>
            </div>

            {errors.insurance && (
              <p className="text-red-500 text-xs mt-1">
                {errors.insurance.message}
              </p>
            )}

            {errors.insurance_file && (
              <p className="text-red-500 text-xs mt-1">
                {
                  errors.insurance_file
                    .message
                }
              </p>
            )}

            <FileBadge
              fileName={
                watch("insurance_file")
                  ?.name ||
                (!removedFiles.insurance &&
                  user?.kyc_details
                    ?.insurance_image
                    ?.split("/")
                    .pop())
              }
              fileUrl={
                !removedFiles.insurance
                  ? user?.kyc_details
                      ?.insurance_image
                  : null
              }
              fileObject={watch(
                "insurance_file"
              )}
              onRemove={() =>
                removeFile(
                  "insurance_file",
                  "insurance"
                )
              }
            />
          </div>

          {/* Other Documents */}
          <div>
            <label className="text-sm font-medium">
              {t("other_documents")}
            </label>

            <div className="relative mt-2">
              {/* Text Input */}
              <input
                type="text"
                placeholder={t(
                  "enter_document_details_upload_files"
                )}
                {...register(
                  "other_documents_detail"
                )}
                className="w-full border border-gray-300 rounded-lg px-4 py-3 pr-44 focus:outline-none focus:ring-2"
              />

              {/* Upload Button (image style like vehicle) */}
              <label className="absolute right-0 bottom-0 cursor-pointer">
                <img
                  src="/img/icons/choose_file.png"
                  className="h-[49px]"
                  alt={t("choose_file")}
                />

                <input
                  id="otherDocumentFile"
                  type="file"
                  accept="image/*,application/pdf"
                  multiple
                  hidden
                  onChange={(e) =>
                    handleFileChange(
                      e,
                      "other_documents"
                    )
                  }
                />
              </label>
            </div>

            {/* Errors */}
            {(errors.other_documents_detail ||
              errors?.other_documents) && (
              <p className="text-red-500 text-xs mt-1">
                {errors.other_documents_detail
                  ?.message ||
                  errors.other_documents
                    ?.message ||
                  t("invalid_file_type")}
              </p>
            )}

            {/* Preview (same functionality, just styled like vehicle) */}
            {other_documents.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-3">
                {other_documents.map(
                  (doc, index) => {
                    const isExisting =
                      !!doc?.id;

                    const file = isExisting
                      ? doc.file
                      : doc;

                    const fileName =
                      isExisting
                        ? doc?.document_name
                        : doc?.name ||
                          t("document");

                    return (
                      <FileBadge
                        key={index}
                        fileName={fileName}
                        fileUrl={file}
                        fileObject={file}
                        onRemove={() =>
                          removeDocument(
                            index,
                            isExisting
                              ? doc.id
                              : ""
                          )
                        }
                      />
                    );
                  }
                )}
              </div>
            )}
          </div>

          {/* Submit */}
          <div className="mt-6 sm:mt-8 md:col-span-2">
            <button
              type="submit"
              className="w-full md:w-[590px] h-[48px] bg-[#F4B11F] text-white rounded-[8px] hover:opacity-90 transition font-bold"
            >
              {t("request_changes")}
            </button>
          </div>
        </form>
      </div>

      {/* Preview Modal */}
      {previewUrl && (
        <div
          className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[9999]"
          onClick={() => setPreviewUrl(null)}
        >
          <div
            className="relative bg-white p-4 rounded-lg max-w-3xl max-h-[90vh] overflow-auto"
            onClick={(e) =>
              e.stopPropagation()
            }
          >
            <button
              className="absolute top-2 right-2 text-white bg-red-500 rounded-full w-8 h-8 flex items-center justify-center font-bold"
              onClick={() =>
                setPreviewUrl(null)
              }
            >
              ✕
            </button>

            {previewUrl
              .toLowerCase()
              .endsWith(".pdf") ? (
              <iframe
                src={previewUrl}
                className="w-[80vw] h-[80vh]"
                title={t("pdf_preview")}
              />
            ) : (
              <img
                src={previewUrl}
                alt={t("preview")}
                className="max-w-full h-auto rounded-md"
              />
            )}
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full text-center shadow-lg relative overflow-hidden">
            <img
              src="/img/logos/logo.png"
              alt="logo"
              className="absolute inset-0 m-auto w-40 opacity-10 pointer-events-none"
            />

            {/* <h3 className="text-xl font-bold text-gray-900 mb-2 relative z-10">
              Account Pending Approval
            </h3> */}

            <p className="text-gray-800 text-base font-medium leading-relaxed relative z-10">
              {t("account_changes_submitted")}{" "}
              <br />
              {t(
                "account_pending_admin_approval"
              )}
            </p>

            <button
              onClick={() => {
                setShowSuccessModal(false);
                localStorage.clear();
                navigate("/sign-in");
              }}
              className="mt-6 px-24 py-2 border border-[#F4B11F] text-[#F4B11F] rounded-md bg-white hover:bg-[#F4B11F] hover:text-white transition relative z-10 font-bold"
            >
              {t("understood")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}